import { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polygon } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { API, STADIA_TILE_URL, emergencyIcon, formatDuration, getSignalColor } from "./utils";
import { HospitalPopup } from "./HospitalPopup";
import { UnitGpsLayer } from "@/components/gps/UnitGpsMarker";
import { HydrantLayer } from "./HydrantLayer";

export function ClassroomScreen({ units, jobs, gpsPositions, agencies }) {
  const [hospitals, setHospitals] = useState([]);
  const [vacBoundaries, setVacBoundaries] = useState([]);
  const [, setTick] = useState(0);
  const gpsNow = Date.now();

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    axios.get(`${API}/hospitals`).then((r) => {
      setHospitals(r.data.filter((h) => h.lat && h.lng));
    }).catch(() => {});
    // Iter 103 — PCT boundaries removed per operator request. VAC response
    // outlines still render as a light dashed overlay.
    axios.get(`${API}/kml/vac-response`).then((r) => setVacBoundaries(r.data)).catch(() => {});
  }, []);

  const activeJobs = jobs.filter((j) => j.assigned_unit_id);
  const defaultCenter = [40.6782, -73.9442];

  const hospIcon = new L.DivIcon({
    html: `<div style="width:22px;height:22px;background:#FF0000;border:2px solid #FFF;border-radius:3px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.5)">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><rect x="10" y="4" width="4" height="16" rx="1" fill="white"/><rect x="4" y="10" width="16" height="4" rx="1" fill="white"/></svg>
    </div>`,
    className: "",
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -13],
  });

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#000" }}>
      {/* Iter 104 — Wall-TV header banner. Big NY-local clock on the left,
          agency banner on the right. Font sizes intentionally massive so
          the display reads from across a room. */}
      <ExternalDisplayBanner agencies={agencies} />
      <div style={{ flex: 1, minHeight: 300, position: "relative" }}>
        <MapContainer center={defaultCenter} zoom={12} style={{ width: "100%", height: "100%" }} zoomControl={true} attributionControl={false}>
          <TileLayer url={STADIA_TILE_URL} />
          <HydrantLayer />
          {vacBoundaries.map((vac) => {
            const positions = (vac.boundaries || []).map((b) => [b.lat, b.lng]);
            return positions.length >= 3 ? (
              <Polygon key={vac.id} positions={positions}
                pathOptions={{ color: "rgba(255,255,255,0.3)", fillColor: "transparent", fillOpacity: 0, weight: 1, dashArray: "6,4" }}>
                <Popup><strong>{vac.name}</strong></Popup>
              </Polygon>
            ) : null;
          })}
          {hospitals.map((h) => (
            <Marker key={h.id} position={[h.lat, h.lng]} icon={hospIcon}>
              <Popup><HospitalPopup h={h} /></Popup>
            </Marker>
          ))}
          {/* Emergency call markers — blue circle with white cross for every active call with a geocoded location. */}
          {activeJobs.filter((j) => j.lat && j.lng).map((j) => (
            <Marker key={j.id} position={[j.lat, j.lng]} icon={emergencyIcon}>
              <Popup>
                <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12 }}>
                  <strong>CAD {j.cad_number}</strong><br/>
                  {j.call_type}<br/>
                  {j.location}{j.apt ? `, APT ${j.apt}` : ""}<br/>
                  {j.assigned_unit_name && <span>Unit: {j.assigned_unit_name}</span>}
                </div>
              </Popup>
            </Marker>
          ))}
          <UnitGpsLayer positions={gpsPositions} now={gpsNow} />
        </MapContainer>
        <div style={{
          position: "absolute", bottom: 10, left: 10, zIndex: 1000,
          background: "rgba(0,0,0,0.8)", padding: "8px 12px", borderRadius: 4,
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "#FFF",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <div style={{ width: 14, height: 14, background: "#FF0000", border: "1px solid #FFF", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "#FFF", fontSize: 10, fontWeight: 700 }}>+</span>
            </div>
            <span>Hospital</span>
          </div>
          {(gpsPositions || []).length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 14, height: 14, background: "#00BFFF", border: "1px solid #FFF", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#000", fontSize: 7, fontWeight: 700 }}>A</span>
              </div>
              <span>Ambulance</span>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", minHeight: 200, maxHeight: "45%", overflow: "hidden", borderTop: "2px solid #444" }}>
        <div style={{
          background: "#00BFFF", color: "#FFF", textAlign: "center",
          padding: "6px 0", fontSize: 14, fontFamily: "'IBM Plex Mono', monospace",
          fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
        }}>
          ACTIVE ASSIGNMENTS
        </div>
        <div style={{ flex: 1, overflow: "auto", background: "#000" }} data-testid="classroom-active-assignments">
          {activeJobs.length === 0 ? (
            <div style={{ padding: 16, color: "#666", fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, textAlign: "center" }}>
              No active assignments
            </div>
          ) : (
            activeJobs.map((j) => {
              const unit = units.find((u) => u.id === j.assigned_unit_id);
              const sigColor = getSignalColor(unit?.signal);
              return (
                <div key={j.id} style={{
                  display: "flex", padding: "6px 14px",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 14, color: "#FFF", alignItems: "center",
                }}>
                  <span style={{ minWidth: 110, fontWeight: 700 }}>{j.assigned_unit_name || "—"}</span>
                  <span style={{ minWidth: 50, color: sigColor, fontWeight: 700 }}>{unit ? unit.signal : ""}</span>
                  <span style={{ minWidth: 70 }}>{j.cad_number}</span>
                  <span style={{ flex: 1 }}>{j.location}{j.apt ? `, APT ${j.apt}` : ""}</span>
                  <span style={{ minWidth: 100 }}>{j.call_type}</span>
                  <span style={{ minWidth: 30, textAlign: "center" }}>{j.priority}</span>
                  <span style={{ minWidth: 60, textAlign: "right", color: "#AAA" }}>{formatDuration(unit?.status_changed_at)}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Iter 104 — External Display header banner.
 *
 * Wall-TV friendly. Left tile: big NY-local clock (HH:MM:SS) with the
 * date and timezone underneath. Right tile: agency name(s) that this
 * dispatcher is covering this session. Everything is oversized so the
 * banner reads from across a room.
 *
 * Ticks once per second via its own interval so parent re-renders don't
 * matter (the map's smooth-marker RAF loop can be laggy under load).
 */
function ExternalDisplayBanner({ agencies }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Iter 105 — Fullscreen toggle for wall-mounted TV kiosks. Uses the
  // browser Fullscreen API on `document.documentElement` so the entire
  // dispatcher console (not just the banner) goes edge-to-edge. Listens
  // for `fullscreenchange` so the button label stays honest when the
  // operator presses ESC to exit.
  const [isFs, setIsFs] = useState(() =>
    typeof document !== "undefined" && !!document.fullscreenElement,
  );
  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    } else {
      document.documentElement.requestFullscreen?.({ navigationUI: "hide" }).catch(() => {});
    }
  };

  const timeStr = now.toLocaleTimeString("en-US", {
    timeZone: "America/New_York",
    hour12: false, hourCycle: "h23",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const dateStr = now.toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  }).toUpperCase();

  // ── Agency label — collapse noisy corp names to their common abbreviation
  // when we recognise them so the banner reads clean on a TV. Falls back
  // to the raw name if unknown.
  const ABBREV = {
    "BEDFORD STUYVESANT VOLUNTEER AMBULANCE CORPS": "BSVAC",
    "CENTRAL BROOKLYN VOLUNTEER AMBULANCE CORPS": "CBVAC",
    "GOTHAM VOLUNTEER AMBULANCE CORPS": "GVAC",
  };
  const abbrev = (a) => ABBREV[a] || a;
  const list = Array.isArray(agencies) && agencies.length > 0 ? agencies : [];
  const agencyDisplay = list.length === 0
    ? "DISPATCH CENTER"
    : list.length === 1
      ? abbrev(list[0])
      : list.map(abbrev).join(" · ");

  return (
    <div style={{
      background: "linear-gradient(180deg,#001a2a,#000)",
      borderBottom: "3px solid #00BFFF",
      padding: "12px 24px",
      display: "grid",
      // Iter 104b — 3-column grid so the clock sits DEAD CENTER on the
      // banner regardless of the agency label's width. Left spacer keeps
      // symmetry with the right-aligned agency block.
      gridTemplateColumns: "1fr auto 1fr",
      alignItems: "center",
      gap: 24, minHeight: 92,
      fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
      position: "relative",
    }} data-testid="external-display-banner">
      {/* Iter 105 — Fullscreen toggle. Absolute-positioned so it doesn't
          disturb the centered clock. */}
      <button
        data-testid="external-display-fullscreen-btn"
        onClick={toggleFullscreen}
        title={isFs ? "Exit fullscreen (ESC)" : "Go fullscreen"}
        style={{
          position: "absolute", top: 8, left: 12,
          background: isFs ? "#FFCC00" : "transparent",
          color: isFs ? "#000" : "#00BFFF",
          border: `1px solid ${isFs ? "#FFCC00" : "#00BFFF"}`,
          borderRadius: 3,
          padding: "5px 12px",
          fontSize: 11, fontWeight: 800, letterSpacing: 2,
          cursor: "pointer",
          fontFamily: "'IBM Plex Sans', sans-serif",
          textTransform: "uppercase",
        }}
      >
        {isFs ? "⤢ Exit Fullscreen" : "⤢ Go Fullscreen"}
      </button>
      <div />
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1, alignItems: "center", textAlign: "center" }}>
        <div style={{
          fontSize: 68, fontWeight: 800, color: "#00BFFF",
          letterSpacing: 4, textShadow: "0 0 18px rgba(0,191,255,0.35)",
        }} data-testid="external-display-clock">
          {timeStr}
        </div>
        <div style={{ fontSize: 14, color: "#7AB8E8", letterSpacing: 3, marginTop: 6 }}>
          {dateStr} · AMERICA/NEW YORK
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", lineHeight: 1 }}>
        <div style={{ fontSize: 12, color: "#7AB8E8", letterSpacing: 4, marginBottom: 6 }}>
          COVERING
        </div>
        <div style={{
          fontSize: list.length > 1 ? 30 : 44, fontWeight: 800, color: "#FFF",
          letterSpacing: 2, textAlign: "right",
        }} data-testid="external-display-agency">
          {agencyDisplay}
        </div>
      </div>
    </div>
  );
}
